import { SafePromise } from '@benzinga/safe-await';
import { IngressUser, Session } from '@benzinga/session';
import { RestfulClient } from '@benzinga/session';
import {
  UserAvatarPost,
  UserEditInfo,
  UserAvatar,
  SegmentProfileTraits,
  NotificationChannelsResponse,
  ToggleSubscriptionResponse,
  ToggleSubscriptionRequest,
  TOSAcceptance,
} from './entities';
import { UserEnvironment } from './environment';

export interface SegmentProfileCursor {
  url: string;
  has_more: boolean;
  next: string;
  limit: number;
}

export interface SegmentProfileResponse {
  traits?: SegmentProfileTraits;
  cursor?: SegmentProfileCursor;
}

export class UserRestful extends RestfulClient {
  constructor(session: Session) {
    super(session.getEnvironment(UserEnvironment).url, session, { authorizationSession: true });
  }

  updateAvatar = (avatarPost: UserAvatarPost): SafePromise<UserAvatar> => {
    const url = this.URL(`api/v1/chat/identity/${avatarPost.uuid}/avatar/`);

    const formData = new FormData();
    formData.append('avatar', avatarPost.file);
    const data = {
      body: formData,
      disableContentType: true,
      method: 'POST',
    };
    //return this.post(url, data);
    return this.jsonFetch(url.toString(), data);
  };

  editUser = (user: UserEditInfo): SafePromise<IngressUser> => {
    const url = this.URL('api/v1/account/me/');
    return this.patch(url, user);
  };

  getGlobalSettings = (): SafePromise<object> => {
    const url = this.URL('api/v1/account/settings/');
    return this.get(url, { includeHeader: { 'x-session-key': true } });
  };

  setGlobalSetting = (key: string, value: unknown): SafePromise<undefined> => {
    const url = this.URL(`api/v1/account/settings/${key}/`);
    return this.post(url, value, {
      includeHeader: {
        referer: url.toString(),
        'x-csrf-token': true,
        'x-session-key': true,
      },
    });
  };

  declareMarketInterest = (interest: string) => {
    const url = this.URL('lavapress/api/user/onboarding/declared-market-interest');
    return this.post(url, {
      params: {
        onboarding_declared_market_interest: interest,
      },
    });
  };

  deleteAccount = (): SafePromise<unknown> => {
    const url = this.URL('/api/v1/account/me');
    return this.delete(url, { credentials: 'include' });
  };

  getSegmentUserTraits = (params?: { userId?: string; anonymousId?: string }): SafePromise<SegmentProfileResponse> => {
    const hostname = this.session.getEnvironment(UserEnvironment).segmentUrl;
    const url = new URL('/api/segment/user-traits', hostname);
    if (params?.userId) url.searchParams.set('user_id', String(params.userId));
    if (params?.anonymousId && !params.userId) url.searchParams.set('anonymous_id', String(params.anonymousId));

    return this.get(url, {
      allowsAnonymousAuth: true,
      credentials: 'include',
    });
  };

  getNotificationChannels = (): SafePromise<NotificationChannelsResponse> => {
    const url = this.URL('api/v1/account/notification-channels/');
    return this.get(url, {
      credentials: 'include',
      includeHeader: { 'x-session-key': true },
    });
  };

  toggleSubscription = (request: ToggleSubscriptionRequest): SafePromise<ToggleSubscriptionResponse> => {
    const url = this.URL('api/v1/account/notification-channels/toggle/');
    return this.post(url, request, {
      credentials: 'include',
      includeHeader: {
        'x-csrf-token': true,
        'x-session-key': true,
      },
    });
  };

  captureTOSAcceptance = (tosAcceptance: TOSAcceptance): SafePromise<unknown> => {
    const url = this.URL('/api/v1/capture/');
    return this.post(url, tosAcceptance, {
      includeHeader: {
        'x-csrf-token': true,
        'x-session-key': true,
      },
    });
  };
}
